import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wrench } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type {
  DashboardInterventionRowV2Dto,
  DashboardInterventionState,
} from '@/lib/api/v2/dashboard'

/**
 * SOLY-REGIE-006 — « Interventions » on the home page.
 *
 * A list of its OWN, deliberately, and not a widening of « Vos dossiers en cours »: that one is
 * grouped by CLIENT and keeps the most recently active project per client, so folding ~600
 * interventions a year into it would hide the relance of a 25 000 € cuisine behind a retouche
 * opened for the same client — the exact regression the 2026-08-19 review removed. Here one row is
 * one file.
 *
 * It stops at the three states that call for a gesture. A sold and planned intervention belongs to
 * the planning cockpit, and a finished one is already brought back in /invoices by SOLY-REGIE-002's
 * fourth rule. A list that never empties stops being read.
 */

/**
 * The labels deliberately name the GESTURE OWED, not the state reached - « Devis à envoyer » where
 * the commercial-discussion filter says « Devis en cours ». This list exists to be emptied, so it
 * speaks in verbs. No `hint` tooltip: `title` never appears on touch and is not reliably announced
 * by a screen reader, and these three labels read on their own.
 */
const STATE_CONFIG: Record<DashboardInterventionState, { label: string; dotClass: string }> = {
  quote_draft: { label: 'Devis à envoyer', dotClass: 'bg-slate-400' },
  quote_issued: { label: 'Devis sans réponse', dotClass: 'bg-amber-500' },
  to_plan: { label: 'À planifier', dotClass: 'bg-sky-500' },
}

const STATE_ORDER: DashboardInterventionState[] = ['quote_issued', 'quote_draft', 'to_plan']

function daysSince(iso: string | null, now: number): number | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  return Math.max(0, Math.floor((now - then) / 86_400_000))
}

/** « depuis 12 jours » — the only number that matters on a relance. */
function waitingLabel(row: DashboardInterventionRowV2Dto, now: number): string | null {
  const days = daysSince(row.state === 'quote_issued' ? row.quoteIssuedAt : row.createdAt, now)
  if (days == null) return null
  if (days === 0) return "depuis aujourd'hui"
  if (days === 1) return 'depuis hier'
  return `depuis ${days} jours`
}

interface InterventionsSectionProps {
  items: DashboardInterventionRowV2Dto[]
  isLoading: boolean
  /** The server cap cut the list. Saying so is the whole reason the cap reports itself. */
  hasMore?: boolean
  error?: unknown
  onRetry?: () => void
}

export function InterventionsSection({
  items,
  isLoading,
  hasMore,
  error,
  onRetry,
}: InterventionsSectionProps) {
  const navigate = useNavigate()

  // Every hook runs before any early return - React requires the same order on every render.
  //
  // Memoised because this lives inside HomePage, whose client-search state re-renders it on every
  // keystroke, and the list is unpaged. The clock is taken ONCE per mount through a lazy
  // initialiser: reading Date.now() per row during render makes the render impure, so two renders
  // of the same props could disagree. A day boundary crossed while the page sits open is not worth
  // a re-render - the next refetch corrects it.
  const [now] = useState(() => Date.now())
  const ordered = useMemo(() => {
    const rank = (state: DashboardInterventionState) => {
      const i = STATE_ORDER.indexOf(state)
      // An unknown state sorts LAST, never first. The backend ships before the frontend by rule,
      // so this side is the one that meets a value it has never heard of.
      return i === -1 ? STATE_ORDER.length : i
    }
    return [...items].sort((a, b) => rank(a.state) - rank(b.state))
  }, [items])


  // Nothing while loading. This section's designed steady state is ABSENT, so a titled skeleton
  // announces a block that then vanishes on every home load, shifting everything below it to say
  // « never mind ».
  if (isLoading) return null

  // A failure must never look like « rien à faire ». The list empties to nothing on purpose, so
  // silence is its success state — and a 500, or a 404 because the frontend landed before the
  // backend, would be indistinguishable from it. The office would stop relancing its devis and
  // nothing would say why. Every other list on this page already says when it breaks.
  if (error != null) {
    return (
      <section className="space-y-3" data-testid="interventions-section-error">
        <h2 className="text-lg font-semibold">Interventions</h2>
        <div className="rounded-lg border bg-background p-4">
          <div className="text-sm font-medium">Erreur de chargement</div>
          <div className="mt-1 text-sm text-muted-foreground">
            La liste des interventions n&apos;a pas pu être chargée. Elle n&apos;est pas vide — elle
            est inconnue.
          </div>
          {onRetry && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              data-testid="interventions-retry-btn"
              onClick={onRetry}
            >
              Réessayer
            </Button>
          )}
        </div>
      </section>
    )
  }

  // Nothing to show is GOOD news here, and an empty card saying so is noise on a screen that is
  // already dense. The section simply is not there.
  if (items.length === 0) return null

  return (
    <section className="space-y-3" data-testid="interventions-section">
      <div className="flex items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Interventions</h2>
          <p className="text-sm text-muted-foreground">
            {items.length} dossier{items.length > 1 ? 's' : ''} en attente d&apos;un geste
            {hasMore && (
              /* A truncated list presented as a complete one is the same lie as an error shown as
                 emptiness. If this ever appears, the backlog is the problem, not the screen. */
              /* No number. The cap is `InterventionsToHandleQuery.MaxRows` and it is not on the
                 wire, so writing it here is a copy of a server constant that nothing keeps in
                 step: move the cap and the screen states a figure that was never true. */
              <span data-testid="interventions-truncated"> — et d&apos;autres au-delà de cette liste</span>
            )}
          </p>
        </div>
      </div>

      <div className="divide-y overflow-hidden rounded-lg border bg-card">
        {ordered.map((row) => {
          // Without this fallback an unknown state was a TypeError during render, which takes the
          // whole home page down - on the side that meets it first.
          const config = STATE_CONFIG[row.state]
          if (!config) return null
          const waiting = waitingLabel(row, now)

          return (
            <button
              key={row.projectId}
              type="button"
              data-testid={`intervention-row-${row.projectId}`}
              onClick={() => navigate(`/projects/${row.projectId}`)}
              className="flex w-full items-center gap-3 p-2.5 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <Wrench className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {row.projectName?.trim() || row.projectReference}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {row.clientName}
                  {row.projectReference ? ` · ${row.projectReference}` : ''}
                </div>
              </div>

              {waiting && (
                <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                  {waiting}
                </span>
              )}

              {/* The dot sits on the ROW, not inside the badge. Inside `variant="secondary"` -
                  slate-500 in this theme, not the light shadcn chip - the three dots measured
                  1.7:1 to 2.2:1 and the grey one vanished outright, so the only colour-coding in
                  the row was invisible. */}
              <span className="flex shrink-0 items-center gap-1.5">
                <span
                  className={cn('inline-block h-2 w-2 rounded-full', config.dotClass)}
                  aria-hidden="true"
                />
                <Badge variant="secondary" className="rounded-full font-normal">
                  {config.label}
                </Badge>
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}
